const ProcessSection = () => {
  const steps = [
    {
      number: '01',
      title: '無料相談',
      description: 'まずはお気軽にご相談ください。現在の業務課題や自動化したい内容をお聞かせください。',
      details: ['現状の業務フロー確認', '課題の整理', '自動化の可能性診断'],
      icon: 'ri-chat-3-line',
      duration: '30分〜1時間'
    },
    {
      number: '02',
      title: '業務ヒアリング',
      description: '詳細な業務フローをヒアリングし、最適な自動化ポイントを特定します。',
      details: ['業務フロー詳細分析', '関係者へのインタビュー', '技術要件の整理'],
      icon: 'ri-question-answer-line',
      duration: '1〜2週間'
    },
    {
      number: '03',
      title: '自動化設計・提案',
      description: 'ヒアリング内容を基に、最適な自動化プランをご提案いたします。',
      details: ['自動化設計書作成', '費用対効果の算出', 'スケジュール提示'],
      icon: 'ri-file-text-line',
      duration: '3〜5営業日'
    },
    {
      number: '04',
      title: '実装・運用サポート',
      description: '承認後、開発を開始し、運用開始後も継続的にサポートいたします。',
      details: ['システム開発・構築', 'テスト・検証', '運用サポート・保守'],
      icon: 'ri-tools-line',
      duration: '2〜4週間'
    }
  ];

  return (
    <section id="process" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            ご利用までの<span className="text-blue-600">流れ</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            シンプルな4ステップで、業務自動化を実現します
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold">
                      {step.number}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                        <i className={`${step.icon} text-blue-600`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                        <p className="text-sm text-blue-600 font-semibold">{step.duration}</p>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {step.description}
                    </p>
                    
                    <ul className="space-y-2">
                      {step.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-center text-sm text-gray-700">
                          <i className="ri-check-line text-green-600 mr-2"></i>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 矢印の表示(デスクトップのみ) */}
          <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="flex flex-col items-center space-y-8">
              <i className="ri-arrow-down-line text-2xl text-blue-600"></i>
              <i className="ri-arrow-down-line text-2xl text-blue-600"></i>
              <i className="ri-arrow-down-line text-2xl text-blue-600"></i>
            </div>
          </div>
        </div>

        <div className="mt-16 bg-white rounded-xl p-8 shadow-lg">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              まずは無料相談から始めませんか？
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              「何から始めればいいかわからない」「本当に効果があるのか不安」<br />
              そんな方こそ、まずは無料相談をご利用ください。
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => {
                  const event = new Event('openContactForm');
                  window.dispatchEvent(event);
                }}
                className="bg-red-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                今すぐ無料相談
              </button>
            </div>
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-time-line text-2xl text-green-600"></i>
            </div>
            <h4 className="font-bold text-gray-900 mb-2">スピード対応</h4>
            <p className="text-gray-600 text-sm">24時間以内レスポンス</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-check-line text-2xl text-blue-600"></i>
            </div>
            <h4 className="font-bold text-gray-900 mb-2">安心サポート</h4>
            <p className="text-gray-600 text-sm">運用開始後も継続サポート</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-money-dollar-circle-line text-2xl text-purple-600"></i>
            </div>
            <h4 className="font-bold text-gray-900 mb-2">明確な料金</h4>
            <p className="text-gray-600 text-sm">事前見積もりで安心</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
